import { create } from 'zustand';
import type { ChatMessage, ChatSession, Attachment } from '../lib/db';
import { listMessages, listSessions, newSession, putMessage, deleteMessage, getSettings, setSettings as persistSettings } from '../lib/db';
import type { Settings } from '../lib/db';
import type { Mode, Aspect } from '../router/types';

interface State {
  sessions: ChatSession[];
  activeId: string | null;
  messages: ChatMessage[];
  settings: Settings;
  mode: Mode;
  aspect: Aspect;
  durationSec: number;
  modelOverride?: string;
  directorAgent: 'gemini' | 'chatgpt' | 'grok' | 'claude';
  staging: { attachments: Attachment[]; startFrame?: Attachment; endFrame?: Attachment };

  init(): Promise<void>;
  pickSession(id: string): Promise<void>;
  createSession(): Promise<void>;
  appendMessage(m: ChatMessage): Promise<void>;
  updateMessage(id: string, fn: (m: ChatMessage) => ChatMessage): Promise<void>;
  removeMessage(id: string): Promise<void>;
  setMode(m: Mode): void;
  setAspect(a: Aspect): void;
  setDuration(s: number): void;
  setModelOverride(id?: string): void;
  setDirectorAgent(agent: 'gemini' | 'chatgpt' | 'grok' | 'claude'): void;
  setSettings(p: Partial<Settings>): Promise<void>;
  addStaged(a: Attachment): void;
  removeStaged(id: string): void;
  setStartFrame(a?: Attachment): void;
  setEndFrame(a?: Attachment): void;
  clearStaging(): void;
}

let initPromise: Promise<void> | null = null;

export const useSession = create<State>((set, get) => ({
  sessions: [],
  activeId: null,
  messages: [],
  settings: { id: 'settings', theme: 'dark', cloudSync: false },
  mode: 'chat',
  aspect: '1:1',
  durationSec: 6,
  directorAgent: 'gemini',
  staging: { attachments: [] },

  async init() {
    if (initPromise) return initPromise;
    initPromise = (async () => {
      const [sessions, settings] = await Promise.all([listSessions(), getSettings()]);
      let activeId = sessions[0]?.id;
      let isNew = false;
      if (!activeId) {
        const s = await newSession('First session');
        sessions.unshift(s);
        activeId = s.id;
        isNew = true;
      }
      const messages = await listMessages(activeId);
      
      // Check if we already injected demo messages in any session
      const hasDemo = sessions.some(s => s.title === 'Demo Widgets');
      if (!hasDemo) {
        const demoS = await newSession('Demo Widgets');
        
        const m1: ChatMessage = { id: crypto.randomUUID(), role: 'assistant', content: "Here is the high-fidelity image render based on your parameters.", createdAt: Date.now() - 4000, meta: { model: 'ProtoLive Image', provider: 'Demo' }, attachments: [{ id: crypto.randomUUID(), name: 'nikola.jpg', kind: 'image', mime: 'image/jpeg', blob: new Blob([]), source: 'https://theparadoxuniverse.b-cdn.net/_nikola.jpg' }] };
        const m2: ChatMessage = { id: crypto.randomUUID(), role: 'assistant', content: "The cinematic film sequence has been generated successfully.", createdAt: Date.now() - 3000, meta: { model: 'ProtoLive Video', provider: 'Demo' }, attachments: [{ id: crypto.randomUUID(), name: 'coachella.mp4', kind: 'video', mime: 'video/mp4', blob: new Blob([]), source: 'https://theparadoxuniverse.b-cdn.net/!File%20Organization/!ALPHA/Alpha_Animations_Art/coachella.mp4' }] };
        const m3: ChatMessage = { id: crypto.randomUUID(), role: 'assistant', content: "Synthesized audio spatial track successfully processed.", createdAt: Date.now() - 2000, meta: { model: 'ProtoLive Audio', provider: 'Demo' }, attachments: [{ id: crypto.randomUUID(), name: 'nikola_speech.mp3', kind: 'audio', mime: 'audio/mpeg', blob: new Blob([]), source: 'https://theparadoxuniverse.b-cdn.net/A%20rare%20Nikola%20Tesla%20speach.mp3' }] };
        const m4: ChatMessage = { id: crypto.randomUUID(), role: 'assistant', content: "3D procedural asset mapped and ready for viewport.", createdAt: Date.now() - 1000, meta: { model: 'ProtoLive 3D', provider: 'Demo' }, attachments: [{ id: crypto.randomUUID(), name: '3D_Scene.glb', kind: 'glb', mime: 'model/gltf-binary', blob: new Blob([]), source: 'proto://shape/torus' }] };
        const m5: ChatMessage = { id: crypto.randomUUID(), role: 'assistant', content: "Sign in to start creating!", createdAt: Date.now(), meta: {} };

        await putMessage(demoS.id, m1);
        await putMessage(demoS.id, m2);
        await putMessage(demoS.id, m3);
        await putMessage(demoS.id, m4);
        await putMessage(demoS.id, m5);

        sessions.unshift(demoS);
        activeId = demoS.id;
        messages.length = 0;
        messages.push(m1, m2, m3, m4, m5);
      }
      
      set({ sessions, activeId, settings, messages });
      applyTheme(settings.theme);
    })();
    return initPromise;
  },

  async pickSession(id) {
    const messages = await listMessages(id);
    set({ activeId: id, messages });
  },

  async createSession() {
    const s = await newSession('New session');
    set(st => ({ sessions: [s, ...st.sessions], activeId: s.id, messages: [] }));
  },

  async appendMessage(m) {
    const id = get().activeId;
    if (!id) return;
    await putMessage(id, m);
    set(st => ({ messages: [...st.messages, m] }));
  },

  async updateMessage(msgId, fn) {
    const id = get().activeId;
    if (!id) return;
    set(st => {
      const idx = st.messages.findIndex(x => x.id === msgId);
      if (idx === -1) return st;
      const nm = fn(st.messages[idx]);
      putMessage(id, nm).catch(console.error); // optimistic
      const next = [...st.messages];
      next[idx] = nm;
      return { messages: next };
    });
  },

  async removeMessage(msgId) {
    const id = get().activeId;
    if (!id) return;
    await deleteMessage(msgId);
    set(st => ({ messages: st.messages.filter(m => m.id !== msgId) }));
  },

  setMode(mode) {
    set({ mode, aspect: mode === 'video' ? '16:9' : '1:1' });
  },
  setAspect(a) { set({ aspect: a }); },
  setDuration(s) { set({ durationSec: s }); },
  setModelOverride(id) { set({ modelOverride: id }); },
  setDirectorAgent(agent) { set({ directorAgent: agent }); },

  async setSettings(p) {
    const next = await persistSettings(p);
    set({ settings: next });
    applyTheme(next.theme);
  },

  addStaged(a) { set(st => ({ staging: { ...st.staging, attachments: [...st.staging.attachments, a] } })); },
  removeStaged(id) { set(st => ({ staging: { ...st.staging, attachments: st.staging.attachments.filter(x => x.id !== id) } })); },
  setStartFrame(a) { set(st => ({ staging: { ...st.staging, startFrame: a } })); },
  setEndFrame(a) { set(st => ({ staging: { ...st.staging, endFrame: a } })); },
  clearStaging() { set({ staging: { attachments: [] } }); }
}));

function applyTheme(t: Settings['theme']) {
  const root = document.documentElement;
  const resolved = t === 'light' ? 'light' : 'dark';
  root.setAttribute('data-theme', resolved);
}
